'use client';

import * as React from 'react';
import { DataTable } from '@/components/data-table/data-table';
import { useDataTable } from '@/hooks/use-data-table';
import { DataTableSortList } from '@/components/data-table/data-table-sort-list';

import { usePromotionsTableColumns } from './promotions-table-columns';
import { PromotionsTableActions } from './PromotionsTableActions';
import { PromotionsTableActionBar } from './PromotionsTableActionBar';
import { PromotionsTableSkeleton } from './PromotionsTableSkeleton';
import { MainTableState } from '@/lib/utils/table-state-utils';
import type { TableConfig } from '@/types/data-table';
import { DEFAULT_SKELETON_ROWS } from '../../../../../../config/data-table';
import { useNavigate, useParams } from 'react-router-dom';
import { DeletePromotionModal } from './delete-promotion-modal';
import { PromotionItem } from '@/lib/api/promotions';
import { useStoreNavigate } from '@/hooks/use-app-navigate';

interface PromotionsTableProps {
    promotions: PromotionItem[];
    total: number;
    loading: boolean;
    onTableStateChange: (state: MainTableState) => void;
    onRefetch: () => void;
    config?: TableConfig;
}

/**
 * PromotionsTable component for displaying promotions with DataTable functionality
 */
export function PromotionsTable({
    promotions,
    total,
    loading,
    onTableStateChange,
    onRefetch,
    config = {},
}: PromotionsTableProps) {

    const { storeRoutes, navigate, id } = useStoreNavigate();
    const [promotionId, setPromotionId] = React.useState<string | undefined>(undefined)

    const columns = usePromotionsTableColumns({
        storeId: id,
        onEditPromotion: (id: string) => navigate(storeRoutes.DISCOUNT_DETAILS(id)),
        onDeletePromotion: (id: string) => setPromotionId(id),
    })

    const { table } = useDataTable({
        data: promotions,
        columns,
        total,
        config,
        onTableStateChange,
        getRowId: (row: PromotionItem) => row.id,
    });

    return (
        <DataTable
            table={table}
            actionBar={<PromotionsTableActionBar table={table} />}
            isLoading={loading}
            skeletonRows={<PromotionsTableSkeleton rows={config.skeletonRows || DEFAULT_SKELETON_ROWS} />}
        >
            <PromotionsTableActions table={table} onRefresh={onRefetch} isLoading={loading} storeId={id} />
            <DataTableSortList table={table} align="end" />
            <DeletePromotionModal promotionId={promotionId} onClose={() => setPromotionId(undefined)} />
        </DataTable>
    );
}

